import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { PlusIcon, PhoneIcon, FolderIcon, ExclamationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { SortState, ViewMode } from "../common/ListViewControls";
import ItemCard from "../common/ItemCard";
import ListPage, { ListColumn } from "../common/ListPage";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { api, InvestigationListItem } from "./api";
import NewInvestigationModal from "./NewInvestigationModal";
import DeleteInvestigationModal from "./DeleteInvestigationModal";

type SortKey = "name" | "calls" | "leads" | "created" | "lastActivity";

interface Props {
  items: InvestigationListItem[];
  loading: boolean;
  onRefresh: () => Promise<void>;
}

const InvestigationList: React.FC<Props> = ({ items, loading, onRefresh }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<InvestigationListItem | null>(null);
  const deleteInvestigation = async () => {
    if (!deleting) return;
    try {
      await api.remove(deleting.id);
      toast.success("Investigation deleted");
      setDeleting(null);
      await onRefresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not delete investigation");
    }
  };
  // Owners only; stops the row/card click from opening the investigation.
  const deleteButton = (inv: InvestigationListItem, className?: string) =>
    inv.role === "owner" ? (
      <button
        type="button"
        className={cn("p-1 rounded-md text-base-content/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40", className)}
        title="Delete investigation"
        onClick={(e) => {
          e.stopPropagation();
          setDeleting(inv);
        }}
      >
        <TrashIcon className="size-4" />
      </button>
    ) : null;
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>("investigations.viewMode", "grid");
  const [sort, setSort] = useLocalStorageState<SortState<SortKey>>("investigations.sort", { key: "lastActivity", dir: "desc" });
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? items.filter((i) => i.name.toLowerCase().includes(q) || (i.case_number || "").toLowerCase().includes(q)) : items;
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
        case "calls":
          cmp = a.callCount - b.callCount;
          break;
        case "leads":
          cmp = a.newLeadCount - b.newLeadCount;
          break;
        case "created":
          cmp = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        default:
          cmp = new Date(a.lastActivity || 0).getTime() - new Date(b.lastActivity || 0).getTime();
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [items, search, sort]);

  const columns: ListColumn<InvestigationListItem, SortKey>[] = [
    {
      key: "name",
      header: "Name",
      sortKey: "name",
      render: (inv) => (
        <div className="flex items-center gap-2 min-w-0">
          <FolderIcon className="h-5 w-5 text-primary flex-shrink-0" />
          <p className="font-medium truncate">{inv.name}</p>
        </div>
      ),
    },
    { key: "case", header: "Case #", className: "text-sm text-base-content/60 whitespace-nowrap", render: (inv) => inv.case_number || "—" },
    { key: "calls", header: "Calls", sortKey: "calls", defaultDir: "desc", className: "text-sm text-base-content/60", render: (inv) => inv.callCount },
    { key: "files", header: "Files", className: "text-sm text-base-content/60", render: (inv) => inv.fileCount },
    {
      key: "leads",
      header: "New leads",
      sortKey: "leads",
      defaultDir: "desc",
      render: (inv) => (
        <span className={cn("px-2 py-0.5 rounded-full text-xs font-semibold", inv.newLeadCount ? "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400" : "bg-base-200 text-base-content/60")}>
          {inv.newLeadCount}
        </span>
      ),
    },
    {
      key: "created",
      header: "Created",
      sortKey: "created",
      defaultDir: "desc",
      className: "text-sm text-base-content/60 whitespace-nowrap",
      render: (inv) => (inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"),
    },
    {
      key: "lastActivity",
      header: "Updated",
      sortKey: "lastActivity",
      defaultDir: "desc",
      className: "text-sm text-base-content/60 whitespace-nowrap",
      render: (inv) => (inv.lastActivity ? new Date(inv.lastActivity).toLocaleDateString() : "—"),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (inv) => deleteButton(inv, "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"),
    },
  ];

  return (
    <ListPage<InvestigationListItem, SortKey>
      title="Investigations"
      subtitle="Jail calls, documents, and evidence, analyzed and ranked."
      action={
        <Button variant="primary" onClick={() => setCreating(true)}>
          <PlusIcon className="h-5 w-5" /> New Investigation
        </Button>
      }
      items={filtered}
      getKey={(inv) => inv.id}
      columns={columns}
      sort={sort}
      onSort={setSort}
      onRowClick={(inv) => navigate(`/investigations/${inv.id}`)}
      search={{ value: search, onChange: setSearch, placeholder: "Search investigations..." }}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      // Shared card anatomy (common/ItemCard) so Investigations, Forms, and
      // Redactions read as one system.
      renderCard={(inv) => (
        <ItemCard
          onClick={() => navigate(`/investigations/${inv.id}`)}
          icon={<FolderIcon className="h-6 w-6 text-primary" />}
          title={inv.name}
          actions={
            <>
              {inv.newLeadCount > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400">
                  <ExclamationCircleIcon className="h-3 w-3" /> {inv.newLeadCount} new
                </span>
              )}
              {deleteButton(inv, "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity")}
            </>
          }
          subtitle={inv.case_number ? `Case ${inv.case_number}` : "No case number"}
          meta={
            <>
              <span className="inline-flex items-center gap-0.5 text-xs text-base-content/40">
                <PhoneIcon className="h-3 w-3" /> {inv.callCount} calls
              </span>
              <span className="text-xs text-base-content/40">{inv.fileCount} files</span>
              {inv.processingCount > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-amber-950/40 dark:text-amber-400">
                  <span className="size-1.5 rounded-full bg-amber-500 animate-pulse" /> {inv.processingCount} processing
                </span>
              )}
              {inv.created_at && <span className="text-[10px] text-base-content/40">Created {new Date(inv.created_at).toLocaleDateString()}</span>}
              {inv.lastActivity && <span className="text-[10px] text-base-content/40">Updated {new Date(inv.lastActivity).toLocaleDateString()}</span>}
            </>
          }
        />
      )}
      loading={loading}
      isFiltered={Boolean(search.trim())}
      noMatch={`No investigations match "${search.trim()}"`}
      empty={{
        icon: <FolderIcon className="size-10" />,
        title: "No investigations yet",
        description: "Create one, then drop in a folder of jail calls.",
        action: (
          <Button variant="primary" onClick={() => setCreating(true)}>
            New Investigation
          </Button>
        ),
      }}
    >
      <DeleteInvestigationModal investigation={deleting} onClose={() => setDeleting(null)} onConfirm={deleteInvestigation} />

      <NewInvestigationModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={async (created) => {
          await onRefresh();
          setCreating(false);
          navigate(`/investigations/${created.id}/files`);
        }}
      />
    </ListPage>
  );
};

export default InvestigationList;
